import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import axiosInstance from '../../_helpers/axios_instance';

export interface StressorCategory {
  id: number;
  name: string;
  description?: string;
  enabled?: boolean;
}

type AsyncStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

interface StressorCategoriesState {
  categories: StressorCategory[];
  status: AsyncStatus;
  error: string | string[] | null;
}

const initialState: StressorCategoriesState = {
  categories: [],
  status: 'idle',
  error: null,
};

export const fetchStressorCategories = createAsyncThunk<
  StressorCategory[],
  void,
  { state: RootState }
>(
  'stressorCategories/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/api/static_comments/categories');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.errors || error.message);
    }
  },
  {
    condition: (_, { getState }) => getState().stressor_categories.status !== 'loading',
  }
);

const stressorCategoriesSlice = createSlice({
  name: 'stressorCategories',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchStressorCategories.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchStressorCategories.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // Only surface enabled categories to users. Disabled categories are
        // excluded from the assessment picker and Enrichment Center filters.
        // (A missing `enabled` flag is treated as enabled for safety.)
        state.categories = action.payload
          .filter((category) => category.enabled !== false)
          .sort((a, b) => a.name.localeCompare(b.name));
      })
      .addCase(fetchStressorCategories.rejected, (state, action) => {
        state.status = 'failed';
        state.error = (action.payload as string | string[]) ?? null;
      });
  },
});

export default stressorCategoriesSlice.reducer;